import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api, fileUrl } from '../lib/api';
import {
  Camera, Image as ImageIcon, X, ChevronLeft, ChevronDown, MapPin, Check, Send, Loader2,
  AlertOctagon, AlertTriangle, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const SEVERITIES = [
  { key: 'critical', label: 'Critical', icon: AlertOctagon,  chip: 'bg-red-100 text-red-800 border-red-300' },
  { key: 'major',    label: 'Major',    icon: AlertTriangle, chip: 'bg-amber-100 text-amber-800 border-amber-300' },
  { key: 'minor',    label: 'Minor',    icon: AlertCircle,   chip: 'bg-slate-100 text-slate-700 border-slate-300' },
];

const PRIORITIES = [
  { key: 'high',   label: 'High' },
  { key: 'normal', label: 'Medium' },
  { key: 'low',    label: 'Low' },
];

const MAX_PHOTOS = 5;

export default function MobileSnag() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [photos, setPhotos] = useState([]); // { local: File, previewUrl, uploaded_file_id? }
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [severity, setSeverity] = useState('major');
  const [priority, setPriority] = useState('normal');
  const [vendorId, setVendorId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [description, setDescription] = useState('');
  const [showDesc, setShowDesc] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/properties').catch(() => ({ data: [] })),
      api.get('/vendors').catch(() => ({ data: [] })),
    ]).then(([p, v]) => {
      setProperties(p.data);
      setVendors(v.data);
      if (p.data.length && !propertyId) setPropertyId(p.data[0].id);
    });
  }, []); // eslint-disable-line

  const handleFiles = (files) => {
    const arr = Array.from(files || []);
    const room = MAX_PHOTOS - photos.length;
    const take = arr.slice(0, room);
    if (arr.length > room) toast.error(`Only ${MAX_PHOTOS} photos max — took ${room}.`);
    const enriched = take.map(f => ({ local: f, previewUrl: URL.createObjectURL(f) }));
    setPhotos([...photos, ...enriched]);
  };

  const removePhoto = (i) => {
    const p = photos[i];
    if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    setPhotos(photos.filter((_, idx) => idx !== i));
  };

  const uploadPhoto = async (photo) => {
    const fd = new FormData();
    fd.append('file', photo.local, photo.local.name || 'snag.jpg');
    const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data.file_id;
  };

  const submit = async () => {
    if (!title.trim()) return toast.error('Please add a title');
    if (!propertyId) return toast.error('Please choose a property');
    setSubmitting(true);
    try {
      // Upload all photos first
      setUploading(true);
      const fileIds = [];
      for (const p of photos) {
        try {
          const id = await uploadPhoto(p);
          if (id) fileIds.push(id);
        } catch { /* ignore individual failures */ }
      }
      setUploading(false);

      // Build snag payload
      const payload = {
        title: title.trim(),
        location: location.trim() || null,
        severity,
        priority,
        vendor_id: vendorId || null,
        property_id: propertyId,
        description: description.trim() || null,
        photo_file_ids: fileIds,
        photo_url: fileIds.length ? fileUrl(fileIds[0]) : null,
      };
      const r = await api.post('/snags', payload);
      toast.success('Snag logged');
      navigate(`/app/properties/${propertyId}?tab=snags&open=${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log snag');
    } finally { setSubmitting(false); setUploading(false); }
  };

  const canAddMore = photos.length < MAX_PHOTOS;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F1EB] to-[#E9E5DE] pb-40" data-testid="mobile-snag-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-xl border-b border-white/70 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-slate-100" data-testid="mobile-snag-back">
          <ChevronLeft size={22}/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="overline text-[9px]">On-site snag</div>
          <div className="font-serif text-lg leading-tight">Log a snag</div>
        </div>
        <button onClick={submit} disabled={submitting || uploading}
                data-testid="mobile-snag-save"
                className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5 disabled:opacity-50">
          {submitting ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
          {uploading ? 'Uploading…' : submitting ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="px-4 pt-4 space-y-4">
        {/* Photos */}
        <section data-testid="mobile-snag-photos">
          <div className="text-xs font-medium text-slate-700 mb-2 flex items-center justify-between">
            <span>Photos <span className="text-slate-400 font-normal">· {photos.length}/{MAX_PHOTOS}</span></span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-2xl overflow-hidden bg-slate-200 shadow-sm">
                <img src={p.previewUrl} alt="" className="w-full h-full object-cover"/>
                <button onClick={() => removePhoto(i)}
                        data-testid={`mobile-snag-photo-remove-${i}`}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center">
                  <X size={12}/>
                </button>
              </div>
            ))}
            {canAddMore && (
              <>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="mobile-snag-camera-btn"
                  className="aspect-square rounded-2xl border-2 border-dashed border-[#679286]/60 bg-white/60 flex flex-col items-center justify-center gap-1 text-[#679286]"
                >
                  <Camera size={22}/>
                  <span className="text-[10px] font-medium">Camera</span>
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  data-testid="mobile-snag-gallery-btn"
                  className="aspect-square rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 flex flex-col items-center justify-center gap-1 text-slate-500"
                >
                  <ImageIcon size={22}/>
                  <span className="text-[10px] font-medium">Gallery</span>
                </button>
              </>
            )}
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </section>

        {/* Title */}
        <FieldCard label="Title">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Kitchen tile cracked"
            data-testid="mobile-snag-title"
            className="w-full text-base bg-transparent outline-none placeholder:text-slate-400"
          />
        </FieldCard>

        {/* Property */}
        <FieldCard label="Property">
          <select
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            data-testid="mobile-snag-property"
            className="w-full text-base bg-transparent outline-none"
          >
            {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </FieldCard>

        {/* Location */}
        <FieldCard label="Location on-site" iconRight={<MapPin size={14} className="text-slate-400"/>}>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Master bedroom / Balcony / Kitchen wall…"
            data-testid="mobile-snag-location"
            className="w-full text-base bg-transparent outline-none placeholder:text-slate-400"
          />
        </FieldCard>

        {/* Severity */}
        <div>
          <div className="text-xs font-medium text-slate-700 mb-2">Severity</div>
          <div className="grid grid-cols-3 gap-2" data-testid="mobile-snag-severity-group">
            {SEVERITIES.map(s => {
              const Icon = s.icon;
              const on = severity === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSeverity(s.key)}
                  data-testid={`mobile-snag-severity-${s.key}`}
                  className={`aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                    on ? s.chip + ' shadow-md scale-[1.03]' : 'border-slate-200 bg-white/70 text-slate-500'
                  }`}
                >
                  <Icon size={22}/>
                  <span className="text-xs font-semibold">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Priority */}
        <div>
          <div className="text-xs font-medium text-slate-700 mb-2">Priority</div>
          <div className="grid grid-cols-3 gap-2" data-testid="mobile-snag-priority-group">
            {PRIORITIES.map(p => {
              const on = priority === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPriority(p.key)}
                  data-testid={`mobile-snag-priority-${p.key}`}
                  className={`py-3 rounded-2xl border-2 transition-all ${
                    on ? 'border-[#679286] bg-[#EEF2F0] text-[#4d7266] shadow-md' : 'border-slate-200 bg-white/70 text-slate-500'
                  }`}
                >
                  <span className="text-sm font-semibold">{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Vendor */}
        <FieldCard label="Assign to vendor (optional)">
          <select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            data-testid="mobile-snag-vendor"
            className="w-full text-base bg-transparent outline-none"
          >
            <option value="">— Not yet assigned —</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.company || v.name} · {v.trade}</option>)}
          </select>
        </FieldCard>

        {/* Description (collapsible) */}
        <button
          onClick={() => setShowDesc(s => !s)}
          className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/60 border border-white/70 text-sm text-slate-700"
          data-testid="mobile-snag-desc-toggle"
        >
          <span className="font-medium">Description <span className="text-slate-400 font-normal">(optional)</span></span>
          <motion.div animate={{ rotate: showDesc ? 180 : 0 }}><ChevronDown size={16}/></motion.div>
        </button>
        <AnimatePresence>
          {showDesc && (
            <motion.textarea
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional context (what happened, when, contractor already contacted, etc.)"
              data-testid="mobile-snag-description"
              className="w-full p-4 rounded-2xl bg-white/60 border border-white/70 text-sm outline-none placeholder:text-slate-400 resize-none"
            />
          )}
        </AnimatePresence>
      </div>

      {/* Sticky bottom Save (in case scroll is long) */}
      <div className="fixed left-0 right-0 bottom-0 z-30 pb-[env(safe-area-inset-bottom)] p-3">
        <button
          onClick={submit}
          disabled={submitting || uploading || !title.trim()}
          data-testid="mobile-snag-save-bottom"
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#1A2530] to-[#3a4a58] text-white font-medium shadow-2xl disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {uploading ? <><Loader2 size={16} className="animate-spin"/> Uploading photos…</>
            : submitting ? <><Loader2 size={16} className="animate-spin"/> Saving…</>
            : <><Check size={16}/> Log snag</>}
        </button>
      </div>
    </div>
  );
}

function FieldCard({ label, children, iconRight }) {
  return (
    <div className="p-4 rounded-2xl bg-white/60 border border-white/70">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
        {iconRight}
      </div>
      {children}
    </div>
  );
}
